/**
 * t191u/t192u: generated wording that has to read right in en-GB.
 * - aOrAn: "an audio call", "a video call", "an 8-pallet vehicle", "a user", "an hour".
 * - callDuration: "45 secs", "1 min", "2 mins", "1 hr 5 mins", "2 hrs".
 * - callNoteText: call system notes as they should read, also for notes stored before the fix ("started a audio call",
 *   "call ended (2 min)"); applied when a note is shown (chat, previews), so stored messages are left alone.
 */

/** Words starting with a vowel letter but a consonant sound, and the reverse. */
const CONSONANT_SOUND = /^(?:uni(?!nf|nt|nh|ns|nv|nd|nl|nm|nr|nw)|use|usu|ura|uro|eu|ewe|one|once|ufo|uk\b|us\b)/i;
const VOWEL_SOUND = /^(?:hour|honest|honou?r|heir)/i;

export function aOrAn(word: string): 'a' | 'an' {
  const w = word.trim();
  if (!w) return 'a';
  const num = w.match(/^\d+/);
  if (num) {
    // Said aloud: 8, 11, 18, 80-89, 800…, 8000…, 11,000… take "an".
    const n = num[0];
    return /^8/.test(n) || ((n.length === 2 || n.length === 5 || n.length === 8) && /^1[18]/.test(n)) ? 'an' : 'a';
  }
  if (VOWEL_SOUND.test(w)) return 'an';
  if (CONSONANT_SOUND.test(w)) return 'a';
  // Initialisms said letter by letter (an MOT, an SOP) - all capitals, short.
  if (/^[A-Z]{2,5}\b/.test(w)) return /^[AEFHILMNORSX]/.test(w) ? 'an' : 'a';
  return /^[aeiou]/i.test(w) ? 'an' : 'a';
}
/** "an audio call", "a video call". */
export const withArticle = (phrase: string) => `${aOrAn(phrase)} ${phrase}`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** How long a call lasted, en-GB style: under a minute in seconds, then minutes, then hours and minutes. */
export function callDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return plural(s, 'sec', 'secs');
  const m = Math.round(s / 60);
  if (m < 60) return plural(m, 'min', 'mins');
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${plural(h, 'hr', 'hrs')} ${plural(r, 'min', 'mins')}` : plural(h, 'hr', 'hrs');
}

/** A call system note as it should read. `durationSeconds` (newer notes) wins over the minutes in the text. */
export function callNoteText(body: string, durationSeconds?: number | null): string {
  let t = body.replace(/\b(a|an) (audio|video)\b/gi, (_m, a: string, w: string) => {
    const art = aOrAn(w);
    return `${a[0] === 'A' ? art[0].toUpperCase() + art.slice(1) : art} ${w}`;
  });
  t = t.replace(/^(call ended \()(\d+) mins?\b/i, (_m, head: string, n: string) =>
    `${head}${durationSeconds != null && Number.isFinite(durationSeconds) ? callDuration(durationSeconds) : callDuration(Number(n) * 60)}`);
  return t;
}

/**
 * t195u: task references (e.g. TASK-384) are not shown to people. Older stored system notes ("created task TASK-384:
 * Restack bay 12", "turned this into task TASK-384") are rewritten for display; the stored text is left alone.
 */
export function stripTaskRefs(text: string): string {
  return text
    .replace(/\bturned this into task [A-Z][A-Z0-9]*-\d+\b/g, 'turned this into a task')
    .replace(/\b(created|completed|reopened|assigned) task [A-Z][A-Z0-9]*-\d+:\s*/g, '$1 task: ');
}
/** A system note as people see it: call wording (articles, durations) and no task references. */
export const systemNoteText = (text: string, durationS?: number | null) => stripTaskRefs(callNoteText(text, durationS));

/**
 * t546u: system notes written as "<verb> …" ("set this channel to create a journey from each submission", "closed the
 * ad-hoc meeting room") say who did it: the sender's name goes in front. Notes that read on their own (journey updates,
 * birthdays, calls, meeting notes) are left alone.
 */
const ACTOR_SYSTEM_EVENTS = new Set(['form.journey_link', 'form.assigned', 'room.closed', 'room.reopened', 'task.created', 'task.completed', 'task.created_from_message']);
export function systemNoteActor(m: { kind: string; sender_id: string | null; metadata?: unknown }): string | null {
  if (m.kind !== 'system' || !m.sender_id) return null;
  const ev = (m.metadata as { event?: unknown } | null | undefined)?.event;
  return typeof ev === 'string' && ACTOR_SYSTEM_EVENTS.has(ev) ? m.sender_id : null;
}
